import Swiper from 'swiper';

const SLIDER_REENABLE_INTERVAL = 6000;

export default class Slider {
  /**
   * Create new slider instance
   *
   * @param {import('../ThemeBase').default} theme
   * @param {HTMLElement} element
   */
  constructor(theme, element) {
    this.theme = theme;
    this.element = element;
    this.restartTimer = null;
    this.forcePause = false;

    this.setWidths();

    this.swiper = this.configure({});

    // Do not focus on any links outside the active slide.
    this.element
      .querySelectorAll('.slides li:not(.swiper-slide-active) a')
      .forEach((item) => {
        item.setAttribute('tabIndex', '-1');
      });

    const self = this;
    const paginationContainer = document.querySelector(
      '.slider-pagination-container.slider--' + this.getId()
    );
    const playPauseElement = paginationContainer
      ? paginationContainer.querySelector('.slider-pauseplay a')
      : null;
    const dotsContainer = document.querySelector(
      '.swiper-pagination.slider--' + this.getId()
    );
    const dotLinks = dotsContainer
      ? dotsContainer.querySelectorAll('span')
      : null;

    if (dotLinks != null && dotLinks.length) {
      dotLinks.forEach((dot) => {
        dot.setAttribute(
          'aria-label',
          `${self.theme.translations.slide} ${dot.innerText}`
        );
      });
    }

    /**
     * @todo This selector could be more targeted to only return the els from current and next sections
     */
    this.allFocusableEls = Array.from(
      document.querySelectorAll(
        'a:not([tabindex="-1"]), area[href], input:not([type=hidden]):not([tabindex="-1"]), select:not([tabindex="-1"]), textarea:not([tabindex="-1"]), button:not([tabindex="-1"]), iframe:not([tabindex="-1"]), object, embed, [contenteditable]'
      )
    ).filter(
      (el) =>
        !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') != 'true'
    );

    this.playpause = {
      btn_label: self.theme.translations.pause,
      btn_el: playPauseElement,
      is_pressed: 'false'
    };

    this.initPlaypause();
    this.attachResize();
  }

  setWidths = () => {
    // Prevent flickering in the slider by setting fixed width of the slides
    this.element.querySelectorAll('.slides li').forEach((slide) => {
      const currentSlide = /** @type {HTMLLIElement} */ (slide);
      currentSlide.style.width = this.element.clientWidth + 'px';
    });
  }

  attachResize = () => {
    if (!this.swiper) return;
    let self = this;

    window.addEventListener('resize', function() {
      self.swiper.update();
    });

    window.addEventListener('update-swipers', function() {
      self.swiper.update();
    });
  };

  initPlaypause = () => {
    const self = this;
    if (!this.playpause.btn_el) return;

    this.playpause.btn_index = this.allFocusableEls.indexOf(
      this.playpause.btn_el
    );
    this.playpause.nextFocusableEl = this.allFocusableEls[
      this.playpause.btn_index + 1
    ];
    this.playpause.prevFocusableEl = this.allFocusableEls[
      this.playpause.btn_index - 1
    ];

    this.playpause.btn_el.setAttribute('role', 'button');
    this.playpause.btn_el.setAttribute('aria-label', this.playpause.btn_label);
    this.playpause.btn_el.setAttribute(
      'aria-pressed',
      this.playpause.is_pressed
    );

    this.playpause.btn_el.addEventListener('click', function(e) {
      e.preventDefault();
      const targetLink = /** @type {HTMLAnchorElement} */ (e.target);

      if (targetLink.classList.contains('slider-pause')) {
        self.pauseSwiper();
      } else {
        self.startSwiper();
      }
    });

    this.playpause.btn_el.addEventListener('keydown', function(e) {
      e.preventDefault();
      self.playpauseHandleKeydown(e);
    });
  };

  playpauseHandleKeydown = (e) => {
    const isTabKey = e.code == 'Tab' ? true : false;
    const isEnterKey = e.code == 'Enter' ? true : false;

    if (isTabKey) {
      if (e.shiftKey) {
        this.playpause.prevFocusableEl.focus();
      } else {
        this.playpause.nextFocusableEl.focus();
      }
    } else if (isEnterKey) {
      e.target.click();
    } else {
      return;
    }
  };

  setAriaPressed = () => {
    this.playpause.is_pressed = this.playpause.btn_el.classList.contains(
      'slider-pause'
    )
      ? 'false'
      : 'true';

    this.playpause.btn_el.setAttribute(
      'aria-pressed',
      this.playpause.is_pressed
    );
  };

  getSpeed = () => {
    return parseInt(this.element.dataset.sliderSlideTime);
  };

  getId = () => {
    return this.element.dataset.sliderId;
  };

  getSmoothHeightState = () => {
    return window.outerWidth < 767;
  };

  getAnimation = () => {
    return this.element.dataset.sliderAnimation != 'slide'
      ? this.element.dataset.sliderAnimation
      : '';
  };

  getDots = () => {
    return this.element.dataset.sliderDots;
  };

  getArrows = () => {
    return this.element.dataset.sliderArrows;
  };

  currentSlide = () => {
    const activeSlide = /** @type {HTMLDivElement} */ (this.element.querySelector(
      '.swiper-slide-active'
    ));

    return activeSlide.dataset.slideIndex;
  };

  showSlide = (index) => {
    if (this.getSpeed() > 0) {
      this.pauseSwiper();
      this.forcePause = true;
    }
    if (this.currentSlide() !== index) {
      // Change slide without animation
      this.swiper.slideTo(index);
    }
  };

  startAnimation = () => {
    // Don't start animation when autorotate is disabled
    this.forcePause = false;
    if (this.getSpeed() > 0) {
      this.startSwiper();
    }
  };

  startSwiper = () => {
    this.playpause.btn_el.classList.remove('slider-play');
    this.playpause.btn_el.classList.add('slider-pause');
    this.setAriaPressed();
    this.swiper.autoplay.start();
  };

  pauseSwiper = () => {
    this.playpause.btn_el.classList.remove('slider-pause');
    this.playpause.btn_el.classList.add('slider-play');
    this.setAriaPressed();
    this.swiper.autoplay.pause();
  };

  onSlideChange = () => {
    const self = this;
    const speed = this.getSpeed();

    this.element.querySelectorAll('.slides li a').forEach((el) => {
      el.setAttribute('tabIndex', '0');
    });

    this.element
      .querySelectorAll('.slides li:not(.swiper-slide-active) a')
      .forEach((el) => {
        el.setAttribute('tabIndex', '-1');
      });

    if (!this.forcePause && !this.swiper.autoplay.running && speed > 0) {
      // restart autoscroll after given interval since last interaction
      clearTimeout(this.restartTimer);
      this.restartTimer = setTimeout(function() {
        self.startSwiper();
      }, Math.max(0, SLIDER_REENABLE_INTERVAL - speed));
    }
  };

  configure = (options) => {
    if (window.innerWidth === 0) {
      // the app is not yet loaded in the admin, retry later
      setTimeout(this.configure.bind(this), 200);
      return;
    }
    const self = this;
    const speed = this.getSpeed();
    const id = this.getId();
    let nextBtn = '';
    let prevBtn = '';

    const paginationEl =
      this.getDots() == 'true' ? `.swiper-pagination.slider--${id}` : '';

    if (this.getArrows() == 'true') {
      nextBtn = '.swiper-button-next';
      prevBtn = '.swiper-button-prev';
    }

    const swiper = new Swiper(this.element, {
      spaceBetween: 0,
      autoplay: {
        delay: speed,
        disableOnInteraction: true
      },
      effect: this.getAnimation(),
      fadeEffect: { crossFade: true },
      pagination: {
        el: paginationEl,
        clickable: true
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn
      },
      on: {
        slideChangeTransitionEnd: function() {
          self.onSlideChange();
        }
      }
    });

    return swiper;
  };
}
